import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, Plus, Users, BookOpen, ArrowRight, Pencil, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GroupFormDialog } from "@/components/GroupFormDialog";
import { RegionDialog } from "@/components/RegionDialog";

import { confirmar } from "@/components/ConfirmDialog";
export default function Groups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [regionOpen, setRegionOpen] = useState(false);

  const load = () => api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const remove = async (g) => {
    if (!(await confirmar(`Excluir a turma "${g.name}"? Os alunos não são apagados, só saem da turma.`))) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Turma excluída."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Turmas</h1>
          <p className="text-slate-500 mt-1">Agrupe alunos, atribua um professor e matricule em cursos.</p>
        </div>
        <Button variant="outline" onClick={() => setRegionOpen(true)} data-testid="btn-region-reorg">Reorganizar por região</Button>
        <RegionDialog open={regionOpen} onOpenChange={setRegionOpen} onDone={load} />
        <Button data-testid="btn-create-group" onClick={() => { setEditing(null); setOpen(true); }} className="text-white font-semibold" style={{ background: "#1E9E2A" }}>
          <Plus className="h-4 w-4 mr-1.5" /> Nova turma
        </Button>
        <GroupFormDialog open={open} onOpenChange={setOpen} group={editing}
          onSaved={(g) => (editing ? load() : navigate(`/admin/turmas/${g.id}`))} />
      </div>

      {groups === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : groups.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50">
          <Boxes className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nenhuma turma criada ainda.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map((g) => (
            <div
              key={g.id}
              role="button"
              tabIndex={0}
              data-testid={`group-card-${g.id}`}
              onClick={() => navigate(`/admin/turmas/${g.id}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/admin/turmas/${g.id}`)}
              className="text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 group cursor-pointer h-full flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="h-10 w-10 rounded-xl grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
                  <Boxes className="h-5 w-5" />
                </div>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); setEditing(g); setOpen(true); }} title="Editar turma" data-testid={`edit-group-${g.id}`} className="p-1.5 text-slate-400 hover:text-[#232D4F]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); remove(g); }} title="Excluir turma" className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <h3 className="font-display font-semibold text-slate-800 line-clamp-1">{g.name}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{g.instructor_name || "Sem professor"}</p>
              {g.representative_name && <p className="text-xs text-[#1E9E2A] mt-0.5">Representante: {g.representative_name}</p>}
              {/* mesmo cartão da área da professora: números grandes embaixo */}
              <div className="mt-auto pt-4 w-full">
                <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-3.5">
                  {[[Users, g.student_count, "alunos", "text-[#1A2340]"], [null, g.active_count ?? 0, "com curso ativo", "text-[#1E9E2A]"], [BookOpen, g.course_count, "cursos", "text-[#1A2340]"]].map(([Ic, n, rot, cor]) => (
                    <div key={rot}>
                      <div className={`font-display text-xl font-bold leading-none ${cor}`}>{Number(n || 0).toLocaleString("pt-BR")}</div>
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">{Ic && <Ic className="h-3 w-3" />}{rot}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-end gap-1 text-xs font-semibold text-[#232D4F]">Abrir turma <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
